import React, { useContext, useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import {
  LayoutDashboard, Package, Truck as TruckIcon, Users,
  Fuel as FuelIcon, BarChart3, Bell,
  Plug, Settings as SettingsIcon, Search, Calendar, LogOut, Activity,
  Receipt, Gauge, Wallet, Trophy, PieChart, Compass, Fuel as FuelWasteIcon,
  ChevronsLeft, ChevronsRight, Clock3, UserCog, VenetianMask, SlidersHorizontal,
  Rocket, Route as RouteIcon, Milestone, Megaphone,
  Navigation, Sparkles, Map as MapIcon, TrendingUp, Landmark,
} from "lucide-react";
import { AuthContext } from "../../context/AuthContext";
import { apiBaseUrl, authHeaders } from "./shared";
import UserMenu from "../../components/common/UserMenu";
import { getImpersonationState, stopImpersonation } from "../../utils/impersonation";
import { useAlerts } from "../../hooks/useAlerts";
import AnnouncementBanner from "../../components/common/AnnouncementBanner";

// Hardcoded, dispatcher-specific menu -- deliberately NOT the generic
// nav_groups/nav_modules metadata model NavigationView.js uses (that's
// built for the admin/table-builder shell, dark-themed, and doesn't map to
// dispatcher concepts like "Trucks & Trailers"). requireRoles=null means
// every authenticated dispatcher/admin sees it; an array means only those
// roles do (mirrors PrivateRoute's own requireRoles/requireAll pattern).
//
// Grouped into sections (rendered as small uppercase labels, see MENU_SECTIONS
// below) instead of one flat 15-item list -- ordered within each section by
// how often a dispatcher/admin actually reaches for it, not alphabetically.
// A section with zero visible items for the current role (e.g. "System" for
// a plain admin) simply doesn't render its label.
const MENU = [
  // Renamed from "Dashboard" -- for admin/super_admin this now renders
  // ControlTower.jsx (management-by-exception: what's wrong right now,
  // what it's costing, where to look first), not the old comparative
  // scorecard. Same path/route, label-only change, see DashboardHome.jsx.
  { label: "Control Tower", icon: LayoutDashboard, path: "/dispatch", section: null },

  // Regrouped from the old flat Operations/Insights/Admin sections into
  // Dispatch/Optimization/Analytics/Administration, per the Control Tower
  // nav proposal -- pure relabeling + reordering so same-section items stay
  // contiguous (this file groups purely by array order, see hasAccess/
  // rendering below), no routes added/removed, no requireRoles changed.

  // --- Dispatch: the actual day-to-day work surface ---
  // The full-featured board (broker-call matching, negotiation, live HOS
  // map) -- kept exactly as-is by design, not reskinned to match this shell.
  // Navigating here leaves this sidebar entirely (its own deliberately
  // chrome-free, full-viewport layout), same as clicking any other real
  // top-level route.
  { label: "Operational Center", icon: Activity, path: "/dispatch/operational-center", section: "Dispatch" },
  // One shared weekly board (real loads + Home/Shop/PTO/Other status),
  // replacing each dispatcher's own personal spreadsheet -- same audience
  // as the Operational Center itself, so no requireRoles restriction here
  // either (the route guard in App.js still enforces dispatcher/admin/super_admin).
  { label: "Load Board", icon: Calendar, path: "/dispatch/load-board", section: "Dispatch" },
  { label: "En Route to Pickup", icon: Navigation, path: "/dispatch/en-route-to-pickup", requireRoles: ["dispatcher", "admin", "super_admin"], section: "Dispatch" },
  { label: "Loads", icon: Package, path: "/dispatch/loads", section: "Dispatch" },
  { label: "Trucks & Trailers", icon: TruckIcon, path: "/dispatch/trucks", section: "Dispatch" },
  { label: "Drivers", icon: Users, path: "/dispatch/drivers", section: "Dispatch" },
  { label: "Alerts", icon: Bell, path: "/dispatch/alerts", section: "Dispatch" },

  // --- Optimization: plan-vs-actual / exception subsystems ---
  { label: "Fuel Compliance", icon: FuelIcon, path: "/dispatch/fuel-compliance", requireRoles: ["dispatcher", "admin", "super_admin"], section: "Optimization" },
  { label: "Toll Compliance", icon: Milestone, path: "/dispatch/toll-compliance", requireRoles: ["dispatcher", "admin", "super_admin"], section: "Optimization" },
  // Scoped server-side to the caller's own trucks for a plain dispatcher
  // (admin/super_admin still see everything) -- same ownership rule
  // /fleet/alerts already uses, see each scripted API's own sip_code.
  { label: "Route History", icon: Compass, path: "/dispatch/route-history", requireRoles: ["dispatcher", "admin", "super_admin"], section: "Optimization" },
  { label: "Truck History Map", icon: MapIcon, path: "/dispatch/truck-map", requireRoles: ["admin", "super_admin"], section: "Optimization" },
  { label: "Idle Movement", icon: FuelWasteIcon, path: "/dispatch/idle-miles", requireRoles: ["dispatcher", "admin", "super_admin"], section: "Optimization" },
  { label: "HOS Compliance", icon: Gauge, path: "/dispatch/hos-compliance", requireRoles: ["dispatcher", "admin", "super_admin"], section: "Optimization" },
  { label: "ETA Drift History", icon: Clock3, path: "/dispatch/eta-drift-history", requireRoles: ["dispatcher", "admin", "super_admin"], section: "Optimization" },

  // --- Analytics: financial/performance reporting ---
  // Consolidated Finance dashboard -- one tabbed module (Overview + Profitability/
  // Fuel/Tolls/Mileage/Cost Model/Infrastructure) folding the scattered finance
  // reports together. Admin/super_admin only (fleet admin), read-only.
  { label: "Finance", icon: Landmark, path: "/dispatch/finance", requireRoles: ["admin", "super_admin"], section: "Analytics" },
  // Read-only AI assistant -- answers any fleet question with sources.
  // Admin/super_admin only (matches POST /fleet/assistant's server-side gate).
  { label: "Fleet Assistant", icon: Sparkles, path: "/dispatch/assistant", requireRoles: ["admin", "super_admin"], section: "Analytics" },
  // Weekly leaderboard -- open to dispatchers on purpose (it's a
  // competition board, everyone sees everyone's numbers), unlike the
  // "my trucks only" reports above.
  { label: "Performance", icon: Trophy, path: "/dispatch/performance", requireRoles: ["dispatcher", "admin", "super_admin"], section: "Analytics" },
  // Dispatcher-scoped to their own loads (loads.dispatcher_id) server-side,
  // same convention as Route History/Idle Miles/HOS Compliance above --
  // admin/super_admin see every lane, fleet-wide.
  { label: "Lane Profitability", icon: RouteIcon, path: "/dispatch/lane-profitability", requireRoles: ["dispatcher", "admin", "super_admin"], section: "Analytics" },
  { label: "Truck Economics", icon: PieChart, path: "/dispatch/truck-economics", requireRoles: ["admin", "super_admin"], section: "Analytics" },
  // Fuel Savings / Value Intelligence -- read-only, admin/super_admin (fleet admin) only; honest
  // four-tier view (actual paid / observed diff / modeled avoidable / demonstrated=Not established).
  { label: "Fuel Savings", icon: TrendingUp, path: "/dispatch/fuel-savings", requireRoles: ["admin", "super_admin"], section: "Analytics" },
  // Reports and Finance used to be two separate sidebar entries pointing at
  // the exact same FinanceReports component -- merged into one entry.
  // Admin/super_admin only: fleet-wide finance/cost reports, not something
  // a dispatcher needs (their own operational reports are the pages above).
  { label: "Reports", icon: BarChart3, path: "/dispatch/reports", requireRoles: ["admin", "super_admin"], section: "Analytics" },

  // --- Administration (renamed from "Admin") ---
  // First thing a new fleet admin should reach for -- guided walk-through
  // of the same fleet.* properties below, kept ahead of them in the list.
  { label: "Onboarding", icon: Rocket, path: "/dispatch/onboarding", requireRoles: ["admin", "super_admin"], section: "Administration" },
  { label: "Team Members", icon: UserCog, path: "/dispatch/team", requireRoles: ["admin", "super_admin"], section: "Administration" },
  // Publish the sticky banner every dispatcher sees on Dispatch home +
  // Operational Center (see AnnouncementBanner.jsx) -- admin/super_admin
  // only, dispatchers only ever read what's published here (migration
  // 0071's own ACL rows enforce this server-side too).
  { label: "Announcements", icon: Megaphone, path: "/dispatch/announcements", requireRoles: ["admin", "super_admin"], section: "Administration" },
  { label: "Fleet Cost Settings", icon: Wallet, path: "/dispatch/fleet-cost-settings", requireRoles: ["admin", "super_admin"], section: "Administration" },
  { label: "Infrastructure Cost", icon: Receipt, path: "/dispatch/infra-cost-report", requireRoles: ["admin", "super_admin"], section: "Administration" },
  { label: "Fleet Operational Settings", icon: SlidersHorizontal, path: "/dispatch/fleet-operational-settings", requireRoles: ["admin", "super_admin"], section: "Administration" },
  { label: "Integrations", icon: Plug, path: "/dispatch/integrations", requireRoles: ["admin", "super_admin"], section: "Administration" },
  { label: "Settings", icon: SettingsIcon, path: "/dispatch/settings", requireRoles: ["admin", "super_admin"], section: "Administration" },

  // Real per-provider (HERE + Claude) API spend -- super_admin only, not
  // admin, since this is billing-sensitive cost data, not fleet ops.
  // Relabeled from "API Usage" -- this is LLM/AI feature spend, not fleet
  // API integrations (that's covered under Admin -> Integrations already),
  // and the old label read as if it were the same thing.
  { label: "AI Usage & Cost", icon: Receipt, path: "/dispatch/api-usage", requireRoles: ["super_admin"], section: "System" },
  // Relabeled from "Command Center" -- that name collided with the Fleet
  // Dashboard's own <h1>, "Fleet Command Center". This page is actually an
  // infra console (CPU/memory/DB pool + AI spend by feature), so "System
  // Health" says what it is without stepping on the fleet-facing dashboard's
  // name.
  { label: "System Health", icon: Gauge, path: "/dispatch/command-center", requireRoles: ["super_admin"], section: "System" },
];

function hasAccess(item, roles) {
  if (!item.requireRoles) return true;
  return item.requireRoles.some((r) => roles.includes(r));
}

// Persisted across reloads/navigation -- a dispatcher who collapses the
// sidebar for more map/table room shouldn't have it silently pop back open
// on their next page load.
const COLLAPSE_STORAGE_KEY = "dispatch_sidebar_collapsed";

export default function DispatcherLayout() {
  const { user, logout } = useContext(AuthContext);
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const [collapsed, setCollapsed] = useState(() => localStorage.getItem(COLLAPSE_STORAGE_KEY) === "true");
  const [hoveredPath, setHoveredPath] = useState(null);
  const roles = user?.roles || [];
  const { isImpersonating, impersonatedUser } = getImpersonationState();

  const toggleCollapsed = () => {
    setCollapsed((c) => {
      localStorage.setItem(COLLAPSE_STORAGE_KEY, String(!c));
      return !c;
    });
  };

  // Same counts the Alerts page itself shows -- just enough here to tell a
  // dispatcher at a glance whether it's worth clicking through, not a full
  // duplicate of that page's list.
  const { counts: alertCounts, total: totalAlerts } = useAlerts(apiBaseUrl, authHeaders);

  return (
    <div style={styles.shell}>
      <aside style={{ ...styles.sidebar, ...(collapsed ? styles.sidebarCollapsed : {}) }}>
        <div style={{ ...styles.logo, ...(collapsed ? styles.logoCollapsed : {}) }}>
          <img src="/logo-mark.png" alt="" style={styles.logoMark} />
          {!collapsed && <span style={styles.logoText}>AIVAN</span>}
        </div>
        <nav style={styles.nav}>
          {(() => {
            const visible = MENU.filter((item) => hasAccess(item, roles));
            let lastSection;
            return visible.map((item) => {
              const Icon = item.icon;
              const showLabel = !collapsed && item.section && item.section !== lastSection;
              lastSection = item.section;
              return (
                <React.Fragment key={item.path}>
                  {showLabel && <div style={styles.navSectionLabel}>{item.section}</div>}
                  <div
                    style={styles.navItemWrap}
                    onMouseEnter={() => collapsed && setHoveredPath(item.path)}
                    onMouseLeave={() => setHoveredPath(null)}
                  >
                    <NavLink
                      to={item.path}
                      end={item.path === "/dispatch"}
                      style={({ isActive }) => ({
                        ...styles.navItem,
                        ...(collapsed ? styles.navItemCollapsed : {}),
                        ...(isActive ? styles.navItemActive : {}),
                      })}
                    >
                      {({ isActive }) => (
                        <>
                          {isActive && <span style={styles.navItemActiveBar} />}
                          <Icon size={17} strokeWidth={isActive ? 2.3 : 1.9} />
                          {!collapsed && <span>{item.label}</span>}
                        </>
                      )}
                    </NavLink>
                    {collapsed && hoveredPath === item.path && (
                      <div style={styles.navTooltip}>{item.label}</div>
                    )}
                  </div>
                </React.Fragment>
              );
            });
          })()}
        </nav>
        <button
          style={{ ...styles.collapseToggle, ...(collapsed ? styles.collapseToggleCollapsed : {}) }}
          onClick={toggleCollapsed}
          title={collapsed ? "Expand sidebar" : "Collapse sidebar"}
        >
          {collapsed ? <ChevronsRight size={14} /> : <><ChevronsLeft size={14} /><span>Collapse</span></>}
        </button>
        <div style={{ ...styles.sidebarFooter, ...(collapsed ? styles.sidebarFooterCollapsed : {}) }}>
          <div style={styles.userRow}>
            <div style={styles.userAvatar}>{(user?.email || "?")[0].toUpperCase()}</div>
            {!collapsed && (
              <div style={{ minWidth: 0 }}>
                <div style={styles.userName}>{user?.email}</div>
                <div style={styles.userRole}>{roles[0] || "user"}</div>
              </div>
            )}
          </div>
          <button style={styles.logoutBtn} onClick={() => { logout(); navigate("/login"); }} title="Log out">
            <LogOut size={15} />
          </button>
        </div>
      </aside>

      <div style={styles.main}>
        <header style={styles.topbar}>
          <div style={styles.searchWrap}>
            <Search size={14} color="var(--muted)" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search anything…"
              style={styles.searchInput}
            />
          </div>
          <div style={styles.topbarRight}>
            <Calendar size={16} color="var(--muted)" />
            <button
              style={styles.bellBtn}
              onClick={() => navigate("/dispatch/alerts")}
              title={totalAlerts > 0 ? `${totalAlerts} alert${totalAlerts === 1 ? "" : "s"}` : "Alerts"}
            >
              <Bell size={16} color="var(--muted)" />
              {totalAlerts > 0 && (
                <span style={{ ...styles.bellBadge, background: alertCounts.critical > 0 ? "#dc2626" : "#a16207" }}>
                  {totalAlerts > 9 ? "9+" : totalAlerts}
                </span>
              )}
            </button>
            {isImpersonating && (
              <div style={styles.impersonationChip}>
                <VenetianMask size={13} />
                <span>
                  Impersonating <strong>{impersonatedUser?.sip_name || impersonatedUser?.email}</strong>
                </span>
                <button type="button" onClick={stopImpersonation} style={styles.stopImpersonationBtn}>
                  Stop
                </button>
              </div>
            )}
            <UserMenu apiBaseUrl={apiBaseUrl} isImpersonating={isImpersonating} theme="light" />
          </div>
        </header>
        <div style={styles.content}>
          <AnnouncementBanner apiBaseUrl={apiBaseUrl} authHeaders={authHeaders} />
          <Outlet />
        </div>
      </div>
    </div>
  );
}

// Sidebar deliberately stays a distinct dark navy (#0b1220) rather than
// the app's --brand blue -- matches the provided redesign mockup, kept as
// an intentional dark-sidebar/light-content split rather than forcing the
// brand color onto it. Turns out to be a near-exact match for the real
// AIVAN logo mark's own background (see /logo-mark.png), so the sidebar
// icon sits naturally against it with no visible edge. Main content
// honors the real, already-live index.css light token system
// (--bg/--card/--ink/--line/--radius/--shadow), not the orphaned
// theme.css/theme.js files (confirmed unused anywhere).
const SIDEBAR_TRANSITION = "width .18s ease";

const styles = {
  shell: { display: "flex", height: "100vh", width: "100vw", overflow: "hidden", fontFamily: "inherit" },
  sidebar: {
    width: 232, flexShrink: 0, background: "#0b1220", color: "#e2e8f0",
    display: "flex", flexDirection: "column", padding: "18px 12px",
    borderRight: "1px solid rgba(255,255,255,0.06)", transition: SIDEBAR_TRANSITION,
  },
  sidebarCollapsed: { width: 72, padding: "18px 10px" },

  logo: { display: "flex", alignItems: "center", gap: 10, padding: "0 8px", marginBottom: 26 },
  logoCollapsed: { justifyContent: "center", padding: 0 },
  logoMark: {
    width: 30, height: 30, borderRadius: 8, boxShadow: "0 2px 10px rgba(29, 111, 216, 0.45)",
    objectFit: "cover", flexShrink: 0,
  },
  logoText: { fontSize: 14, fontWeight: 800, letterSpacing: ".02em" },

  nav: { display: "flex", flexDirection: "column", gap: 2, flex: 1, minHeight: 0, overflowY: "auto", overflowX: "hidden" },
  navSectionLabel: {
    fontSize: 10, fontWeight: 700, color: "#4b5a72", textTransform: "uppercase", letterSpacing: 0.8,
    padding: "16px 10px 6px",
  },
  navItemWrap: { position: "relative" },
  navItem: {
    position: "relative", display: "flex", alignItems: "center", gap: 10, padding: "9px 10px", borderRadius: 9,
    color: "#8695ab", fontSize: 13, fontWeight: 600, textDecoration: "none",
    transition: "background .15s ease, color .15s ease",
  },
  navItemCollapsed: { justifyContent: "center", padding: "10px 0" },
  navItemActive: { background: "rgba(29, 111, 216,0.16)", color: "#f1fbf9" },
  navItemActiveBar: {
    position: "absolute", left: -10, top: "50%", transform: "translateY(-50%)",
    width: 3, height: "62%", borderRadius: "0 4px 4px 0", background: "var(--brand)",
  },
  navTooltip: {
    position: "absolute", left: "calc(100% + 12px)", top: "50%", transform: "translateY(-50%)",
    background: "#1c2536", color: "#e8edf4", padding: "6px 10px", borderRadius: 7,
    fontSize: 12, fontWeight: 600, whiteSpace: "nowrap", boxShadow: "0 8px 24px rgba(0,0,0,0.4)",
    border: "1px solid rgba(255,255,255,0.08)", zIndex: 60, pointerEvents: "none",
  },

  collapseToggle: {
    display: "flex", alignItems: "center", gap: 7, width: "100%", marginTop: 6, marginBottom: 2,
    background: "transparent", border: "none", color: "#4b5a72", fontSize: 11.5, fontWeight: 600,
    cursor: "pointer", padding: "8px 10px", borderRadius: 8, textAlign: "left",
  },
  collapseToggleCollapsed: { justifyContent: "center", padding: "8px 0" },

  sidebarFooter: {
    borderTop: "1px solid rgba(255,255,255,0.08)", paddingTop: 12, marginTop: 8,
    display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8,
  },
  sidebarFooterCollapsed: { flexDirection: "column", gap: 10 },
  userRow: { display: "flex", alignItems: "center", gap: 8, minWidth: 0 },
  userAvatar: {
    width: 28, height: 28, borderRadius: "50%", background: "linear-gradient(135deg, var(--brand), #12459e)", color: "#fff",
    display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12, fontWeight: 700, flexShrink: 0,
    boxShadow: "0 0 0 2px rgba(255,255,255,0.08)",
  },
  userName: { fontSize: 12, fontWeight: 600, color: "#e2e8f0", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", maxWidth: 120 },
  userRole: { fontSize: 10.5, color: "#64748b", textTransform: "capitalize" },
  logoutBtn: { background: "none", border: "none", color: "#64748b", cursor: "pointer", padding: 6, flexShrink: 0 },

  main: { flex: 1, display: "flex", flexDirection: "column", minWidth: 0, background: "var(--bg)" },
  topbar: {
    display: "flex", alignItems: "center", justifyContent: "space-between", padding: "12px 24px",
    background: "var(--card)", borderBottom: "1px solid var(--line)", flexShrink: 0,
  },
  searchWrap: {
    display: "flex", alignItems: "center", gap: 8, background: "var(--bg)", border: "1px solid var(--line)",
    borderRadius: "var(--radius-md)", padding: "7px 12px", width: 320,
  },
  searchInput: { border: "none", outline: "none", background: "transparent", fontSize: 13, color: "var(--ink)", width: "100%" },
  topbarRight: { display: "flex", alignItems: "center", gap: 16 },
  bellBtn: {
    position: "relative", display: "flex", alignItems: "center", justifyContent: "center",
    background: "none", border: "none", cursor: "pointer", padding: 2,
  },
  bellBadge: {
    position: "absolute", top: -6, right: -7, minWidth: 15, height: 15, borderRadius: 999,
    color: "#fff", fontSize: 9.5, fontWeight: 700, display: "flex", alignItems: "center",
    justifyContent: "center", padding: "0 3px", border: "1.5px solid var(--card)",
  },
  impersonationChip: {
    display: "flex", alignItems: "center", gap: 8, padding: "6px 10px", borderRadius: 999,
    background: "rgba(245, 158, 11, 0.95)", color: "#111827", fontSize: 12, fontWeight: 800,
    boxShadow: "0 2px 10px rgba(245,158,11,.28)",
  },
  stopImpersonationBtn: {
    border: "none", background: "#111827", color: "#fff", borderRadius: 999,
    padding: "3px 8px", cursor: "pointer", fontSize: 11, fontWeight: 900,
  },
  content: { flex: 1, overflowY: "auto", padding: 24 },
};
